import {
  add,
  clampToZero,
  computeBucketDiscountsWithAmounts,
  fromIqd,
  fromMillidinars,
  multiply,
  percentage,
  roundCheckoutTotal,
  subtract,
  sum,
  type BucketAmounts,
  type BucketDiscounts,
  type BucketPercents,
  type Millidinars,
  type RoundingRule,
  type ServiceChargeSetting,
  type TabDetailDto,
} from '@gc/shared';

/**
 * THE TILL'S ARITHMETIC — what the checkout drawer quotes the customer, with
 * no React in it, so it can be pinned figure by figure.
 *
 * It restates `TabsService.checkout` step for step and must agree with it to
 * the millidinar: the drawer's Due is what the cashier says out loud, and the
 * server's figure is what the box is reconciled against. The API suite
 * `tabs-checkout-service-charge.test.ts` drives the real checkout with the
 * payload `detail()` produces and asserts this same arithmetic; this module's
 * own suite pins the same worked figures from this side.
 */

type TabSessionLine = TabDetailDto['sessions'][number];

export type CheckoutRoundType = 'SINGLE' | 'DOUBLE' | 'THREE_BALL';

export interface CheckoutRoundsEntry {
  /** Raw input-field text; parsed with the same forgiveness as the discount. */
  rounds: string;
  type: CheckoutRoundType;
}

/**
 * One single round, matching `ReservationDrawer`'s default of '1': a billiard
 * table on the tab bills at least a game unless staff zero it on purpose —
 * silence must never mean free.
 */
export const DEFAULT_ROUNDS_ENTRY: CheckoutRoundsEntry = { rounds: '1', type: 'SINGLE' };

/** The per-type ceiling in `checkoutTabSchema.rounds`, restated for the input. */
export const MAX_ROUNDS = 999;

/**
 * One row's games, as a number the contract will accept.
 *
 * CLAMPED AT BOTH ENDS. `checkoutTabSchema` caps each count at 999 and this
 * had no ceiling, so a cashier who typed 1000 games on a billiard table — or
 * leant on the spinner — got the same opaque "Some fields need fixing" that
 * cost an evening on `selection.sessionIds`. The field is the single funnel for
 * both the Due preview and the posted payload, so clamping HERE keeps the
 * figure on screen equal to the figure charged, to the millidinar.
 */
const countOf = (entry: CheckoutRoundsEntry): number =>
  Math.min(MAX_ROUNDS, Math.max(0, Math.floor(Number(entry.rounds) || 0)));

/**
 * The ROUND-billed sessions this checkout will close inline. Their games are
 * counted NOW or billed as zero — `Session.singleRounds` is only ever written
 * at close, so an open table's row carries nothing to fall back on.
 */
export function openRoundSessions(sessions: readonly TabSessionLine[]): TabSessionLine[] {
  return sessions.filter(
    (session) =>
      session.reservationType === 'ROUND' &&
      (session.status === 'ACTIVE' || session.status === 'PAUSED'),
  );
}

/**
 * The `rounds` half of `POST /tabs/:id/checkout` — `{ [sessionId]: counts }`,
 * the shape `checkoutTabSchema.rounds` takes. Before the drawer sent this,
 * every billiard table settled through a party checkout billed 0.
 */
export function buildRoundsPayload(
  sessions: readonly TabSessionLine[],
  entries: Record<string, CheckoutRoundsEntry>,
): Record<string, { singleRounds: number; doubleRounds: number; threeBallRounds: number }> {
  const rounds: Record<
    string,
    { singleRounds: number; doubleRounds: number; threeBallRounds: number }
  > = {};
  for (const session of openRoundSessions(sessions)) {
    const entry = entries[session.id] ?? DEFAULT_ROUNDS_ENTRY;
    const count = countOf(entry);
    rounds[session.id] = {
      singleRounds: entry.type === 'SINGLE' ? count : 0,
      doubleRounds: entry.type === 'DOUBLE' ? count : 0,
      threeBallRounds: entry.type === 'THREE_BALL' ? count : 0,
    };
  }
  return rounds;
}

/** What one open table's entered rounds will bill, off its own snapshot prices. */
function roundsFor(
  session: TabSessionLine,
  entries: Record<string, CheckoutRoundsEntry>,
): Millidinars {
  const entry = entries[session.id] ?? DEFAULT_ROUNDS_ENTRY;
  const price =
    entry.type === 'DOUBLE'
      ? session.doublePriceMd
      : entry.type === 'THREE_BALL'
        ? (session.threeBallPriceMd ?? '0')
        : session.singleGamePriceMd;
  return multiply(fromMillidinars(price), countOf(entry));
}

/**
 * What the entered rounds will bill, so the Due preview covers the billiard
 * tables too. Priced off each session's own snapshot columns — the same
 * figures `computeSessionAmount` charges from.
 */
export function roundsPreviewMd(
  sessions: readonly TabSessionLine[],
  entries: Record<string, CheckoutRoundsEntry>,
): Millidinars {
  return sum(openRoundSessions(sessions).map((session) => roundsFor(session, entries)));
}

/** One open order bill as `GET /tabs/:id` reports it — see `TabOrderPreviewLine`. */
export interface CheckoutPreviewOrder {
  billId: string;
  invoiceNumber: string;
  totalMd: string;
  cafeBaseMd: string;
  kitchenBaseMd: string;
  shishaBaseMd: string;
  /**
   * The venue's service charge on this bill, and the bases with it riding in
   * them. OPTIONAL: an API from before the service charge sends none, and a
   * bill with none previews exactly as it always did.
   */
  serviceChargeMd?: string;
  cafeBaseWithServiceMd?: string;
  kitchenBaseWithServiceMd?: string;
  shishaBaseWithServiceMd?: string;
}

/** The slice of the tab detail the arithmetic reads. */
export interface CheckoutPreviewTab {
  sessions: readonly TabSessionLine[];
  orders: readonly CheckoutPreviewOrder[];
  roundingRule: RoundingRule;
  checkoutRoundingStepIqd: number;
  /** Optional for the same older-API reason as the order fields above. */
  serviceCharge?: ServiceChargeSetting;
}

/**
 * What a partial settlement covers — the devices and orders the cashier ticked
 * on the Outstanding screen. Null settles the whole party, which is what the
 * device board's wallet action has always done.
 */
export interface CheckoutSelection {
  sessionIds: readonly string[];
  orderBillIds: readonly string[];
}

export interface CheckoutPreviewInput {
  selection: CheckoutSelection | null;
  roundEntries: Record<string, CheckoutRoundsEntry>;
  percents: BucketPercents;
  bucketAmounts: BucketAmounts;
  /** The counter's whole percent off the lot. */
  discountPercent: number;
  /** The counter's typed amount off the lot, whole IQD. */
  discountIqd: number;
  /** The "Remove service charge" switch. */
  serviceChargeWaived: boolean;
}

export interface CheckoutPreview {
  settleSessions: TabSessionLine[];
  settleOrders: CheckoutPreviewOrder[];
  roundsMd: Millidinars;
  /** The open orders being settled, as they stand (their own discounts in). */
  ordersMd: Millidinars;
  /** Everything owed BEFORE the service charge and the till's discounts. */
  owedMd: Millidinars;
  /** The service charge this checkout will add — 0 when waived. */
  serviceChargeMd: Millidinars;
  /**
   * What it would be if NOT waived. Non-zero is what shows the switch at all:
   * a 0% venue, or a selection wholly outside the scope, has nothing to remove.
   */
  serviceChargeAvailableMd: Millidinars;
  buckets: BucketDiscounts;
  percentDiscountMd: Millidinars;
  rawDueMd: Millidinars;
  dueMd: Millidinars;
  /** What the venue's rounding moved; its own line when non-zero. */
  roundingMd: Millidinars;
}

const ZERO = fromMillidinars(0n);

/** Whole percent of one bill's GROSS amount, halves away from zero — `serviceChargeOn`. */
function serviceChargeOn(grossMd: Millidinars, percent: number): Millidinars {
  return percent > 0 ? percentage(grossMd, Math.min(percent, 100)) : ZERO;
}

/**
 * The "+" a service charge wears on the receipts and screens — and nothing in
 * front of one handed back. The charge is signed on purpose (a return settled
 * on its own gives its service back) and the formatter already writes that
 * minus, so a bare "+" printed "+-500".
 */
export function serviceChargeSign(md: string | bigint): '+' | '' {
  return BigInt(md) < 0n ? '' : '+';
}

export function previewCheckout(
  tab: CheckoutPreviewTab,
  input: CheckoutPreviewInput,
): CheckoutPreview {
  /*
   * A partial settlement discounts and charges ONLY what was ticked, so every
   * base below is narrowed to the selection before any arithmetic. Narrowing
   * here rather than adjusting the totals afterwards is what keeps this preview
   * equal to the till: the server does exactly the same thing, by building its
   * plans from the selected rows and leaving the rest of the money block alone.
   */
  const settleSessions = tab.sessions.filter(
    (session) =>
      !session.settled &&
      (input.selection === null || input.selection.sessionIds.includes(session.id)),
  );
  const settleOrders = tab.orders.filter(
    (order) => input.selection === null || input.selection.orderBillIds.includes(order.billId),
  );

  const venue = tab.serviceCharge ?? { percent: 0, scope: 'BOTH' as const };
  const onDevices = venue.scope === 'BOTH' || venue.scope === 'RESERVATIONS';
  const applies = !input.serviceChargeWaived;

  // The rounds being entered are play the server has not seen yet — they join
  // the device's figures exactly as checkout's inline close will bill them.
  const rounds = new Map(
    openRoundSessions(settleSessions).map((session) => [
      session.id,
      roundsFor(session, input.roundEntries),
    ]),
  );
  const roundsMd = sum(rounds.values());

  // Per-session `totalMd` is `applyRounding(amount - discount)` — the exact
  // per-device figure the till charges. Summing THOSE is the only way a subset
  // preview matches; summing amounts and rounding once at the end does not.
  const devices = settleSessions.map((session) => {
    const extra = rounds.get(session.id) ?? ZERO;
    const total = add(fromMillidinars(session.totalMd), extra);
    // On the GROSS amount, before the device's own discount — as checkout.
    const atVenueRate = onDevices
      ? serviceChargeOn(add(fromMillidinars(session.amountMd), extra), venue.percent)
      : ZERO;
    return { total, atVenueRate, charged: add(total, applies ? atVenueRate : ZERO) };
  });

  const orders = settleOrders.map((order) => {
    const atVenueRate = fromMillidinars(order.serviceChargeMd ?? '0');
    const withService = applies && atVenueRate !== 0n;
    return {
      total: fromMillidinars(order.totalMd),
      atVenueRate,
      charged: add(fromMillidinars(order.totalMd), applies ? atVenueRate : ZERO),
      // The server splits `total + service` by the items' classes; the browser
      // never sees the items, so it takes that split as reported.
      cafe: fromMillidinars(
        withService ? (order.cafeBaseWithServiceMd ?? order.cafeBaseMd) : order.cafeBaseMd,
      ),
      kitchen: fromMillidinars(
        withService ? (order.kitchenBaseWithServiceMd ?? order.kitchenBaseMd) : order.kitchenBaseMd,
      ),
      shisha: fromMillidinars(
        withService ? (order.shishaBaseWithServiceMd ?? order.shishaBaseMd) : order.shishaBaseMd,
      ),
    };
  });

  const serviceChargeAvailableMd = sum([
    ...devices.map((device) => device.atVenueRate),
    ...orders.map((order) => order.atVenueRate),
  ]);
  const serviceChargeMd = applies ? serviceChargeAvailableMd : ZERO;

  const buckets = computeBucketDiscountsWithAmounts(
    {
      // Clamped per device, as checkout's bases are: a negative correction
      // never deepens a discount.
      timeBaseMd: sum(devices.map((device) => clampToZero(device.charged))),
      cafeBaseMd: sum(orders.map((order) => order.cafe)),
      kitchenBaseMd: sum(orders.map((order) => order.kitchen)),
      shishaBaseMd: sum(orders.map((order) => order.shisha)),
    },
    input.percents,
    input.bucketAmounts,
  );

  const ordersMd = sum(orders.map((order) => order.total));
  const owedMd = add(sum(devices.map((device) => device.total)), ordersMd);

  // The counter's percentage comes off what is left AFTER the service charge
  // and the buckets, exactly as `TabsService.checkout` computes it — the two
  // must agree to the millidinar or the drawer quotes a figure the till then
  // refuses to charge.
  const afterBucketsMd = subtract(add(owedMd, serviceChargeMd), buckets.totalDiscountMd);
  const percentDiscountMd = percentage(afterBucketsMd, input.discountPercent);
  const rawDueMd = subtract(
    subtract(afterBucketsMd, percentDiscountMd),
    fromIqd(input.discountIqd),
  );
  // The ONE grand-total rounding pass (§4.1), with the venue's own rule AND its
  // cash step — the same pass checkout charges with.
  const dueMd = roundCheckoutTotal(rawDueMd, tab.roundingRule, tab.checkoutRoundingStepIqd);

  return {
    settleSessions,
    settleOrders: [...settleOrders],
    roundsMd,
    ordersMd,
    owedMd,
    serviceChargeMd,
    serviceChargeAvailableMd,
    buckets,
    percentDiscountMd,
    rawDueMd,
    dueMd,
    roundingMd: subtract(dueMd, rawDueMd),
  };
}
